import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '../lib/supabase';

interface AdminStatusState {
  isAdmin: boolean;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook to check if the current user is an admin
 * Uses Supabase RPC to query admin_users table
 * Caches result to avoid repeated calls
 */
export const useAdminStatus = () => {
  const { user } = useAuth();
  const [state, setState] = useState<AdminStatusState>({
    isAdmin: false,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user?.email) {
        setState({ isAdmin: false, isLoading: false, error: null });
        return;
      }

      try {
        setState((prev) => ({ ...prev, isLoading: true, error: null }));

        const { data, error } = await supabase.rpc('is_admin', {
          user_email: user.email,
        });

        if (error) {
          console.error('[useAdminStatus] Error checking admin status:', error);
          setState({
            isAdmin: false,
            isLoading: false,
            error: error.message,
          });
          return;
        }

        // Only set isAdmin to true if data is explicitly true
        const adminStatus = data === true;

        setState({
          isAdmin: adminStatus,
          isLoading: false,
          error: null,
        });
      } catch (error) {
        console.error('[useAdminStatus] Exception checking admin status:', error);
        setState({
          isAdmin: false,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to check admin status',
        });
      }
    };

    checkAdminStatus();
  }, [user?.email]);

  return state;
};
